/**
 * The DebugOverlay component provides a debug panel that can be toggled
 * on and off. It provides testing tools for QAs and developers
 * No translations are needed for this component
 */
import React, { useState, useEffect, useRef } from 'react';
import styles from './debug-overlay.module.scss';
import { getSubdomainCookie } from 'state/session/helpers';
import classNames from 'classnames';
import {
  storageKeys,
  get as getStorage,
  set as setStorage,
} from 'globals/storage';
import { motion } from 'motion/react';
import * as cookieManager from 'globals/cookieManager';
import { getRootHost } from 'globals/helpers';
import {
  AppLogger,
  DatadogClient,
  DynatraceClient,
  CORRELATION_ID_KEY,
} from '@workivate/tho-web-shared';
import { getStore } from 'app/hermes-redux';
import { logout } from 'state/session/actions';
import type { ConsentType } from 'state/consent/types';
import { updateConsent } from 'state/consent/actions';

const currentDomain = '.' + getRootHost();

export const getEnabledLogFeatures = () => {
  const enabledLogFeatures = localStorage.getItem('enabledLogFeatures');
  return enabledLogFeatures ? JSON.parse(enabledLogFeatures) : {};
};

const handleLogFeaturesChange = (key: string, value: boolean) => {
  const enabledLogFeatures = getEnabledLogFeatures();
  enabledLogFeatures[key] = value;
  localStorage.setItem(
    'enabledLogFeatures',
    JSON.stringify(enabledLogFeatures),
  );
  if (key === storageKeys.debugLogsOn && value === false) {
    localStorage.removeItem('enabledLogFeatures');
  }
};

const DebugLogs = () => {
  const enabledLogFeaturesString = JSON.stringify(getEnabledLogFeatures());
  useEffect(() => {
    const enabledLogFeatures = getEnabledLogFeatures();
    if (enabledLogFeatures.oauth2) {
      AppLogger.setEnabledFeatures({ oauth2: true });
    } else {
      AppLogger.setEnabledFeatures({ oauth2: false });
    }
  }, [enabledLogFeaturesString]);

  const debugLogsOn = localStorage.getItem(storageKeys.debugLogsOn) === 'true';

  return (
    debugLogsOn && (
      <ul style={{ marginLeft: '20px' }}>
        <li>
          <input
            type='checkbox'
            defaultChecked={getEnabledLogFeatures().oauth2}
            onChange={e => handleLogFeaturesChange('oauth2', e.target.checked)}
          />
          oauth2
        </li>
      </ul>
    )
  );
};

interface LocalStoragePanelProps {
  handleCheckboxChange: (key: string, value: boolean) => void;
}

const LocalStoragePanel: React.FC<LocalStoragePanelProps> = ({
  handleCheckboxChange,
}) => {
  const settings = [
    storageKeys.stayOnGenericSubdomain,
    storageKeys.isUnifiedLoginEnabled,
    storageKeys.dataDogEnabled,
    storageKeys.dynatraceEnabled,
    storageKeys.debugLogsOn,
    storageKeys.showDebugOverlayButton,
  ];

  return (
    <div className={styles.panelContents}>
      <ul>
        {settings.map(setting => {
          const initialValue = localStorage.getItem(setting) === 'true';
          return (
            <li key={setting}>
              <label>
                <input
                  type='checkbox'
                  defaultChecked={initialValue}
                  name={setting}
                  id={setting}
                  onChange={e =>
                    handleCheckboxChange(setting, e.target.checked)
                  }
                />
                {setting}
              </label>
              {setting === storageKeys.debugLogsOn && initialValue && (
                <DebugLogs />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

interface CookiePanelProps {
  removeSubdomainCookie: () => void;
}

const CookiePanel: React.FC<CookiePanelProps> = ({ removeSubdomainCookie }) => {
  if (!getSubdomainCookie()) {
    return null;
  }
  return (
    <div className={classNames(styles.panelContents, styles.cookiePanelDiv)}>
      <h3>Cookie</h3>
      <ul>
        <li>
          <div>
            WAM_SUBDOMAIN: {getSubdomainCookie()}
            <button
              onClick={removeSubdomainCookie}
              className={styles.removeButton}
            >
              delete
            </button>
          </div>
        </li>
      </ul>
    </div>
  );
};

interface ActionsPanelProps {
  isOnLocalhost: boolean;
  openUSLRedirect: () => void;
  disableUSLRedirect: () => void;
  resetConsent: (consentType: ConsentType) => void;
  resetWalkthrough: () => void;
}

const ActionsPanel: React.FC<ActionsPanelProps> = ({
  isOnLocalhost,
  openUSLRedirect,
  disableUSLRedirect,
  resetConsent,
  resetWalkthrough,
}) => {
  const tokenRef = useRef<HTMLInputElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const consentSelectRef = useRef<HTMLSelectElement>(null);

  const state = getStore().getState();
  const availableConsents = state.consent.consents.filter(
    consent => consent.consent_type && !consent.mandatory,
  );

  return (
    <div className={styles.panelContents}>
      {isOnLocalhost && (
        <div className={styles.actionColumn}>
          <div className={styles.buttonRow}>
            <button id='redirect-usl-btn' onClick={openUSLRedirect}>
              Set USL to redirect to localhost
            </button>
            <button onClick={disableUSLRedirect}>
              Disable USL local redirect
            </button>
          </div>
          <div>
            <small>
              <i>This action is only visible on localhost</i>
            </small>
          </div>
        </div>
      )}
      <div className={classNames(styles.actionColumn, styles.ottInput)}>
        <select
          name='tokenSelect'
          className={styles.tokenSelect}
          ref={selectRef}
        >
          <option value='x-usl-ott'>x-usl-ott</option>
          <option value='token'>token</option>
          <option value='userTokenOtt'>userToken&ott</option>
          <option value='deeplink'>deeplink</option>
        </select>
        <input ref={tokenRef} type='text' placeholder='Enter Token' />
        <button
          id='login-token-btn'
          onClick={() => {
            if (tokenRef.current) {
              // Get the selected option value
              const selectedOption = selectRef.current?.value.trim();
              const tokenValue = tokenRef.current?.value.trim();
              const newLocation = new URL(window.location.href);

              switch (selectedOption) {
                case 'x-usl-ott':
                  newLocation.searchParams.set('x-usl-ott', tokenValue);
                  break;
                case 'token':
                  newLocation.searchParams.set('token', tokenValue);
                  break;
                case 'userTokenOtt':
                  newLocation.searchParams.set('userToken', tokenValue);
                  newLocation.searchParams.set('ott', 'true');
                  break;
                case 'deeplink':
                  newLocation.searchParams.set('userToken', tokenValue);
                  break;
              }

              window.location.href = newLocation.href;
            }
          }}
        >
          Login with Token
        </button>
      </div>
      <button
        id='logout-btn'
        onClick={async () =>
          await getStore().dispatch(logout({ isHard: false }))
        }
      >
        Logout
      </button>
      {!!availableConsents.length && (
        <div className={styles.consents}>
          <div className={styles.buttonRow}>
            <select name='consentSelect' ref={consentSelectRef}>
              {availableConsents.map(consent => (
                <option key={consent.consent_type} value={consent.consent_type}>
                  {consent.consent_type}
                </option>
              ))}
            </select>
            <button
              id='reset-consent-btn'
              onClick={() => {
                const consentType = consentSelectRef.current
                  ?.value as ConsentType;
                if (consentType) {
                  resetConsent(consentType);
                }
              }}
            >
              Reset Consent
            </button>
          </div>
        </div>
      )}
      <button id='reset-walkthrough-btn' onClick={resetWalkthrough}>
        Reset Walkthrough
      </button>
    </div>
  );
};

const DynatracePanel: React.FC = () => {
  const [dtPropertyKey, setDtPropertyKey] = useState(
    'session_properties.service',
  );
  const [dtPropertyValue, setDtPropertyValue] = useState('');
  const [dtSendStatus, setDtSendStatus] = useState<string | null>(null);

  const [appLogMessage, setAppLogMessage] = useState('');
  const [appLogStatus, setAppLogStatus] = useState<string | null>(null);

  const [dtEventName, setDtEventName] = useState('');
  const [dtEventStatus, setDtEventStatus] = useState<string | null>(null);

  const [dtErrorMessage, setDtErrorMessage] = useState('');
  const [dtErrorStatus, setDtErrorStatus] = useState<string | null>(null);

  const [dtEndSessionStatus, setDtEndSessionStatus] = useState<string | null>(
    null,
  );

  return (
    <div className={styles.panelContents}>
      <div className={styles.dtSection}>
        <small>
          <strong>Session Property</strong>
        </small>
        <div className={styles.dtInlineRow}>
          <input
            type='text'
            value={dtPropertyKey}
            onChange={e => setDtPropertyKey(e.target.value)}
            placeholder='Key (e.g. session_properties.service)'
            className={styles.fullInput}
          />
          <input
            type='text'
            value={dtPropertyValue}
            onChange={e => setDtPropertyValue(e.target.value)}
            placeholder='Value'
          />
          <button
            onClick={() => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const dt = (window as any).dynatrace;
              if (!dt) {
                setDtSendStatus('Error: window.dynatrace not found');
              } else {
                try {
                  dt.sendSessionPropertyEvent?.({
                    [dtPropertyKey]: dtPropertyValue,
                  });
                  setDtSendStatus(
                    `✓ Sent! ${dtPropertyKey}: ${dtPropertyValue}`,
                  );
                } catch (e) {
                  setDtSendStatus(
                    `Error: ${e instanceof Error ? e.message : String(e)}`,
                  );
                }
              }
              setTimeout(() => setDtSendStatus(null), 3000);
            }}
          >
            {dtSendStatus ?? 'Send'}
          </button>
        </div>
      </div>

      <div className={styles.dtSection}>
        <small>
          <strong>AppLogger</strong>{' '}
        </small>
        <div className={styles.dtInlineRow}>
          <input
            type='text'
            value={appLogMessage}
            onChange={e => setAppLogMessage(e.target.value)}
            placeholder='Log message'
            className={styles.fullInput}
          />
          <button
            onClick={() => {
              AppLogger.logWithOptions({
                message: appLogMessage || 'test log from hermes debug panel',
                shouldLogToDynatrace: true,
                context: { source: 'debug-panel' },
              });
              setAppLogStatus(`✓ Sent"`);
              setTimeout(() => setAppLogStatus(null), 3000);
            }}
          >
            {appLogStatus ?? 'Send'}
          </button>
        </div>
      </div>

      <div className={styles.dtSection}>
        <small>
          <strong>Send Event</strong>
        </small>
        <div className={styles.dtInlineRow}>
          <input
            type='text'
            value={dtEventName}
            onChange={e => setDtEventName(e.target.value)}
            placeholder='Event name'
            className={styles.fullInput}
          />
          <button
            onClick={() => {
              try {
                DynatraceClient.sendEvent(
                  dtEventName || 'debug-panel.test-event',
                  { source: 'debug-panel' },
                );
                setDtEventStatus(
                  `✓ Sent: "${dtEventName || 'debug-panel.test-event'}"`,
                );
              } catch (e) {
                setDtEventStatus(
                  `Error: ${e instanceof Error ? e.message : String(e)}`,
                );
              }
              setTimeout(() => setDtEventStatus(null), 3000);
            }}
          >
            {dtEventStatus ?? 'Send'}
          </button>
        </div>
      </div>

      <div className={styles.dtSection}>
        <small>
          <strong>Send Exception</strong>{' '}
          <span className={styles.dtSectionSubtitle}>
            (simulate a Dynatrace error)
          </span>
        </small>
        <div className={styles.dtInlineRow}>
          <input
            type='text'
            value={dtErrorMessage}
            onChange={e => setDtErrorMessage(e.target.value)}
            placeholder='Error message'
            className={styles.fullInput}
          />
          <button
            onClick={() => {
              try {
                DynatraceClient.sendException(
                  new Error(
                    dtErrorMessage || 'debug-panel simulated exception',
                  ),
                  { source: 'debug-panel' },
                );
                setDtErrorStatus(
                  `✓ Sent: "${dtErrorMessage || 'debug-panel simulated exception'}"`,
                );
              } catch (e) {
                setDtErrorStatus(
                  `Error: ${e instanceof Error ? e.message : String(e)}`,
                );
              }
              setTimeout(() => setDtErrorStatus(null), 3000);
            }}
          >
            {dtErrorStatus ?? 'Send'}
          </button>
        </div>
      </div>

      <div className={styles.dtSection}>
        <small>
          <strong>Open Session</strong>
        </small>
        <div className={styles.dtInlineRow}>
          <button
            className={styles.fullWidth}
            onClick={() => {
              const sessionId = DynatraceClient.getSessionId();
              if (!sessionId) return;
              const timestamp = new Date().toISOString();
              const url =
                `https://hmn06273.apps.dynatrace.com/ui/apps/dynatrace.users.sessions` +
                `/session-viewer/${sessionId}/${timestamp}` +
                `?perspective=general&tf=now-8h%3Bnow`;
              window.open(url, '_blank', 'noopener');
            }}
          >
            Open in Dynatrace
          </button>
          <button
            className={styles.dtIconButton}
            title='Copy session link'
            onClick={() => {
              const sessionId = DynatraceClient.getSessionId();
              if (!sessionId) return;
              const timestamp = new Date().toISOString();
              const url =
                `https://hmn06273.apps.dynatrace.com/ui/apps/dynatrace.users.sessions` +
                `/session-viewer/${sessionId}/${timestamp}` +
                `?perspective=general&tf=now-8h%3Bnow`;
              navigator.clipboard.writeText(url);
            }}
          >
            🔗
          </button>
        </div>
      </div>

      <div className={styles.dtSection}>
        <small>
          <strong>End Session</strong>{' '}
          <span className={styles.dtSectionSubtitle}>
            (deletes Dynatrace cookies, reload for a new session)
          </span>
        </small>
        <div className={styles.dtInlineRow}>
          <button
            className={styles.fullWidth}
            onClick={() => {
              const dynatraceCookieNames = [
                'dtCookie',
                'dtPC',
                'dtSa',
                'rxVisitor',
                'rxvt',
              ];
              dynatraceCookieNames.forEach(name => {
                cookieManager.deleteCookie(name);
                cookieManager.deleteCookie(name, currentDomain);
                cookieManager.deleteCookie(name, window.location.hostname);
              });
              setDtEndSessionStatus('✓ Session Ended');
            }}
          >
            {dtEndSessionStatus ?? 'End Session & Delete Cookies'}
          </button>
        </div>
      </div>
    </div>
  );
};

interface CopyPanelProps {
  goToDatadog: (query: string) => void;
}

const CopyPanel: React.FC<CopyPanelProps> = ({ goToDatadog }) => {
  const copyPanelKeys = [
    '_auth_clientId',
    '_auth_idToken',
    '_auth_refreshToken',
    'session_id',
    CORRELATION_ID_KEY,
    'dt_session_id',
    'dd_client_id_query',
  ];
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  let clientId: string | undefined;
  let sessionId: string | undefined;
  let correlationId: string | undefined;

  const copyToClipboard = (key: string, value: string) => {
    navigator.clipboard.writeText(value);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  return (
    <div className={styles.panelContents}>
      <ul>
        {copyPanelKeys.map(key => {
          let value;
          if (key === 'session_id') {
            value = DatadogClient.getSessionId();
            sessionId = value;
          } else if (key === 'dt_session_id') {
            value = DynatraceClient.getSessionId();
          } else if (key === CORRELATION_ID_KEY) {
            value = localStorage.getItem(key);
            correlationId = value || undefined;
          } else {
            value = localStorage.getItem(key);
          }

          if (key === '_auth_clientId' && value) {
            clientId = value;
          }

          if (key === 'dd_client_id_query' && clientId) {
            value = `https://app.datadoghq.com/logs?query=@public_client_id:${clientId}`;
          }

          return (
            value && (
              <li key={key}>
                <span>{key}: </span>
                <input
                  type='text'
                  value={value}
                  readOnly
                  className={styles.fullInput}
                  title={value}
                />

                <button
                  onClick={() => copyToClipboard(key, value)}
                  className={styles.copyPanelButton}
                >
                  {copiedKey === key ? '✔' : 'Copy'}
                </button>
              </li>
            )
          );
        })}
      </ul>
      <div className={styles.datadogButtonsColumn}>
        {!!clientId && (
          <button
            className={styles.datadogButton}
            onClick={() => {
              goToDatadog(`@public_client_id:${clientId}`);
            }}
          >
            Find Datadog logs with client id
          </button>
        )}
        {!!sessionId && (
          <button
            className={styles.datadogButton}
            onClick={() => {
              goToDatadog(`@session_id:${sessionId}`);
            }}
          >
            Find Datadog logs with session id
          </button>
        )}
        {!!sessionId && (
          <button
            className={styles.datadogButton}
            onClick={() => {
              const now = Date.now();
              const rumUrl = `https://app.datadoghq.com/rum/sessions?query=%40type%3Asession%20%40session.id%3A${sessionId}&agg_m=count&agg_m_source=base&agg_t=count&fromUser=false&viz=stream&to_ts=${now}&live=true`;
              window.open(rumUrl, '_blank');
            }}
          >
            Open RUM Explorer with session id
          </button>
        )}
        {!!correlationId && (
          <button
            className={styles.datadogButton}
            onClick={() => {
              goToDatadog(`@${CORRELATION_ID_KEY}:${correlationId}`);
            }}
          >
            Find Datadog logs with correlation id
          </button>
        )}
      </div>
    </div>
  );
};

const DebugOverlay: React.FC = () => {
  const isOnLocalhost = window.origin.includes('localhost');

  const [showDebugOverlay, setShowDebugOverlay] = useState(
    localStorage.getItem(storageKeys.showDebugOverlay) === 'true',
  );
  const [showDebugOverlayButton, setShowDebugOverlayButton] = useState(
    localStorage.getItem(storageKeys.showDebugOverlayButton) === 'true',
  );

  const toggleOverlay = (booleanValue?: boolean | undefined) => {
    if (booleanValue !== undefined) {
      setShowDebugOverlay(booleanValue);
      localStorage.setItem(
        storageKeys.showDebugOverlay,
        booleanValue.toString(),
      );
    } else {
      setShowDebugOverlay(prev => {
        const newValue = !prev;
        localStorage.setItem(storageKeys.showDebugOverlay, newValue.toString());
        return newValue;
      });
    }
  };

  const handleCheckboxChange = (key: string, value: boolean) => {
    const noReloadKeys: string[] = [
      storageKeys.showDebugOverlayButton,
      storageKeys.stayOnGenericSubdomain,
    ];

    localStorage.setItem(key, value.toString());
    if (key === storageKeys.stayOnGenericSubdomain && value === true) {
      cookieManager.deleteCookie('WAM_SUBDOMAIN', currentDomain);
    }
    if (key === storageKeys.debugLogsOn && value === false) {
      localStorage.removeItem('enabledLogFeatures');
    }

    if (key === storageKeys.showDebugOverlayButton) {
      setShowDebugOverlayButton(value);
    }

    if (key === storageKeys.isUnifiedLoginEnabled && value) {
      // let's also enable the oauth2 logging feature when we set USL to enabled
      const enabledLogFeatures = getEnabledLogFeatures();
      enabledLogFeatures.oauth2 = value;
      localStorage.setItem(
        'enabledLogFeatures',
        JSON.stringify(enabledLogFeatures),
      );
      AppLogger.setEnabledFeatures({ oauth2: value });
    }

    // Exclude keys that don't require a reload
    if (!noReloadKeys.includes(key)) {
      window.location.reload();
    }
  };

  const openUSLRedirect = () => {
    window.open(
      'https://usl.enterprise.test.lifeworks.com?redirectTo=development',
      '_blank',
      'noopener',
    );
  };

  const disableUSLRedirect = () => {
    window.open(
      'https://usl.enterprise.test.lifeworks.com?redirectTo=false',
      '_blank',
      'noopener',
    );
  };

  const removeSubdomainCookie = () => {
    cookieManager.deleteCookie('WAM_SUBDOMAIN', currentDomain);

    window.location.reload();
  };

  const goToDatadog = (query: string) => {
    const datadogClientIdUrl = `https://app.datadoghq.com/logs?query=${query}`;
    window.open(datadogClientIdUrl, '_blank');
  };

  const resetConsent = (consentType: ConsentType) => {
    const state = getStore().getState();
    const userId = state.session.user?.user_id;

    const consent = state.consent.consents.find(
      c => c.consent_type === consentType,
    );

    if (consent && userId) {
      // Reset consent by setting all checkboxes to 'none'
      const resetData = {
        consents:
          consent.footers
            ?.filter(f => f.type === 'checkbox')
            .map(() => 'none') || [],
        version: consent.consent_wording_version,
        consent_wording_id: consent.consent_wording_id,
      };

      getStore().dispatch(
        updateConsent(userId, consentType, resetData, false, () => {
          alert(`Consent "${consentType}" has been reset successfully!`);
        }),
      );
    }
  };

  const resetWalkthrough = () => {
    const state = getStore().getState();
    const userId = state.session.user?.user_id;

    if (!userId) {
      alert('No user logged in');
      return;
    }

    const walkthroughExcludeList =
      getStorage<Array<string>>(storageKeys.walkthroughExcludeList, true) || [];

    const updatedList = walkthroughExcludeList.filter(id => id !== userId);

    setStorage<Array<string>>(
      storageKeys.walkthroughExcludeList,
      updatedList,
      true,
    );

    window.location.reload();
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'KeyD' && event.shiftKey) {
        toggleOverlay(undefined);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div>
      {showDebugOverlayButton === true && (
        <div
          className={styles.overlayButton}
          onClick={() => toggleOverlay()}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              toggleOverlay();
            }
          }}
          role='button'
          tabIndex={0}
          title='Debug Panel (Shift + D)'
        >
          🛠️
        </div>
      )}
      {!!showDebugOverlay && (
        <motion.div
          drag='y'
          className={styles.overlay}
          dragMomentum={false}
          dragElastic={0}
        >
          <div className={styles.header}>
            <h2>Debug Panel</h2>

            {/* Add debugging information here */}
            <button
              className={styles['container__close-button']}
              onClick={() => toggleOverlay(false)}
            >
              {' '}
              x{' '}
            </button>
          </div>
          <div className={styles.content}>
            <div className={styles.storagePanel}>
              <h3>Local Storage</h3>
              <LocalStoragePanel handleCheckboxChange={handleCheckboxChange} />
              <CookiePanel removeSubdomainCookie={removeSubdomainCookie} />
            </div>
            <div className={styles.actionsPanel}>
              <h3>Actions</h3>
              <ActionsPanel
                isOnLocalhost={isOnLocalhost}
                openUSLRedirect={openUSLRedirect}
                disableUSLRedirect={disableUSLRedirect}
                resetConsent={resetConsent}
                resetWalkthrough={resetWalkthrough}
              />
            </div>
            <div className={styles.copyPanel}>
              <h3>Copy</h3>
              <CopyPanel goToDatadog={goToDatadog} />
            </div>
            <div className={styles.dynatracePanel}>
              <h3>Dynatrace</h3>
              <DynatracePanel />
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default DebugOverlay;
